import { z } from 'zod';
import type { PortalFilters } from '@/api/portal';
import { resolveRange } from '@/features/analytics/range';
import { inboxSearchFields } from '@/features/inbox/search';
import { requestsSearchFields } from '@/features/requests/search';

/** Parámetros de URL del portal: el rango, los filtros y el cliente elegido viven en la URL (compartible). */
export const portalSearchSchema = z.object({
  tenant: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  agent: z.string().optional(),
  channel: z.enum(['widget', 'whatsapp', 'instagram', 'simulator', 'api']).optional(),
  /**
   * «Incluir conversaciones de prueba» (casilla activada por omisión): solo se escribe en la URL cuando se DESMARCA (`includeTests=false`).
   * Un valor que no sea booleano se ignora (vuelve al valor por omisión).
   */
  includeTests: z.boolean().optional().catch(undefined),
  /** Moneda de los montos: `USD` (por omisión) o `CLP` (si la plataforma cargó el tipo de cambio). */
  currency: z.enum(['USD', 'CLP']).optional().catch(undefined),
  /** Texto a buscar en Leads. */
  q: z.string().max(100).optional().catch(undefined),
  status: z.enum(['open', 'closed', 'handoff']).optional(),
  outcome: z.enum(['lead', 'appointment', 'handoff', 'none']).optional(),
  /** Etiqueta del resumen con IA (clave de la taxonomía del tenant). */
  topic: z
    .string()
    .regex(/^[a-z][a-z0-9_]{0,39}$/)
    .optional()
    .catch(undefined),
  conversation: z.string().optional(),
  /** Solicitudes (`/portal/requests`): vista, tipo, prioridad y la solicitud abierta. */
  ...requestsSearchFields,
  /** Bandeja (`/portal/inbox`): estado de la lista y «solo sin leer». */
  ...inboxSearchFields,
});
export type PortalSearch = z.infer<typeof portalSearchSchema>;

/** Filtros efectivos para la API (con el rango por omisión de 30 días y la zona horaria del tenant). */
export function toPortalFilters(search: PortalSearch, tz: string): PortalFilters {
  const r = resolveRange(search, tz);
  return {
    ...r,
    tz,
    agentId: search.agent,
    channel: search.channel,
    includeTests: search.includeTests === false ? false : undefined,
  };
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const looksLikeId = (s: string | undefined): s is string => s !== undefined && UUID.test(s);
